import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, iif, interval, Observable, of } from 'rxjs';
import { first, map, mergeMap, switchMap, takeWhile } from 'rxjs/operators';
import { KeyValueDataService } from '../database/services/key-value-data.service';
import { QuestionSectionDataService } from '../database/services/question-section-data.service';
import { ExamLogDto } from '../model/exam/log/exam-log-dto';
import { ExamLogType } from '../model/exam/log/exam-log-type.enum';
import { QuestionSectionDto } from '../model/exam/question-section-dto';
import { AutoSaveService } from './autosave.service';
import { DashboardService } from './dashboard.service';

export enum LockedState {
    Unlocked,
    Locked
}

export enum BlurType {
    Body,
    IFrame
}

@Injectable({
    providedIn: 'root'
})
export class LockoutService {
    private blurEvents: BlurType[] = [];
    private blurWatchingActive: boolean = false;

    private readonly keyValueDataService: KeyValueDataService = inject(KeyValueDataService);
    private readonly questionSectionDataService: QuestionSectionDataService = inject(QuestionSectionDataService);
    private readonly router: Router = inject(Router);
    private readonly dashboardService: DashboardService = inject(DashboardService);
    private readonly autoSaveService: AutoSaveService = inject(AutoSaveService);

    public isLocked(): Observable<boolean> {
        return this.keyValueDataService.getLockedState()
            .pipe(map((lockedState: LockedState) => lockedState === LockedState.Locked));
    }

    public lockCandidateOut(): void {
         this.keyValueDataService.getExamCodeChecksum()
             .pipe(switchMap((checksum: string) => iif(
                 () => !!checksum,
                 this.keyValueDataService.getLockedState()
                     .pipe(map((lockedState: LockedState) => {
                         if (lockedState === LockedState.Unlocked) {
                             const log = new ExamLogDto('Candidate has been locked', ExamLogType.CandidateLocked);
                             let logs = [];

                             if (localStorage['examLogs']) {
                             if (JSON.parse(localStorage['examLogs']).length) {
                                 logs = JSON.parse(localStorage['examLogs']);
                                 logs.push(log);
                             } else {
                                 logs = [log];
                             }
                             } else {
                             logs = [log];
                             }

                             localStorage['examLogs'] = JSON.stringify(logs);
                         }
                     }))
                     .pipe(mergeMap(() => this.keyValueDataService.setLockedState(LockedState.Locked)))
                     .pipe(mergeMap(() => this.dashboardService.sendLockedNotification()))
                     .pipe(map(() => this.redirectLocked())),
                     of(null)
             )))
             .pipe(first())
             .subscribe(() => {
                 this.blurEvents.length = 0;
                 this.stopBlurWatching();
             });
    }

    public redirectLocked(): void {
        this.router.navigateByUrl('exam/locked');
    }

    public refreshBlurWatching(): Observable<void> {
        return this.canNavigateAway()
            .pipe(map((canNavigateAway: boolean) => {
                if (canNavigateAway) {
                    this.stopBlurWatching();
                } else {
                    this.startBlurWatching();
                }
            }));
    }

    public startBlurWatching(): void {
        this.keyValueDataService.getExamCodeChecksum().subscribe((checksum: string) => {
            if (!!checksum && !this.blurWatchingActive) {
                this.blurEvents.length = 0;
                this.blurWatchingActive = true;
                this.startBlurWatchingProcess();
            }
        });
    }

    public stopBlurWatching(): void {
        this.blurWatchingActive = false;
    }

    public consumeBlurItems(): boolean {
        if (this.blurEvents.length) {
            const lockCandidateOut: boolean = this.blurEvents.indexOf(BlurType.IFrame) === -1;

            this.blurEvents.length = 0;

            if (lockCandidateOut) {
                this.stopBlurWatching();
            }

            return lockCandidateOut;
        }

        return false;
    }

    public addBlurItem(blurItem: BlurType): void {
        if (this.blurWatchingActive) {
            this.blurEvents.push(blurItem);
        }
    }

    /**
     * Tells if the candidate is permitted to leave the exam window.
     *
     * Each section holds its own option, because a question with a downloadable asset needs
     * the candidate to work in another application. An exam that excludes the section
     * durations is one timed assessment, so one answer must apply to all of it: when any
     * section permits the candidate to leave, the complete exam permits it. An exam that keeps
     * its section durations uses the option of the section that the timer runs.
     */
    private canNavigateAway(): Observable<boolean> {
        return forkJoin({
            excludeSectionDuration: this.keyValueDataService.getExcludeSectionDuration().pipe(first()),
            currentSectionNumber: this.keyValueDataService.getCurrentSectionNumber().pipe(first()),
            sections: this.questionSectionDataService.getAll().pipe(first())
        })
        .pipe(map((data: { excludeSectionDuration: boolean, currentSectionNumber: number, sections: QuestionSectionDto[] }) => {
            if (data.excludeSectionDuration) {
                return data.sections.some((section: QuestionSectionDto) => section.canNavigateAway);
            }

            const currentSection: QuestionSectionDto | undefined = data.sections
                .find((section: QuestionSectionDto) => section.orderIndex === data.currentSectionNumber);

            return !!currentSection?.canNavigateAway;
        }));
    }

    private startBlurWatchingProcess(): void {
        interval(250)
            .pipe(takeWhile(() => this.blurWatchingActive))
            .pipe(map(() => this.consumeBlurItems()))
            .subscribe((lockCandidate: boolean) => {
                if (lockCandidate) {
                    this.autoSaveService.stopAutoSaving();
                    this.lockCandidateOut();
                }
        });
    }
}
